<template>
  <div
    ref="profileScrollEl"
    :class="centerProfileState ? 'flex flex-col' : ''"
    class="h-full min-h-0 overflow-y-auto scroll-smooth pr-1 scrollbar-light scrollbar-gutter-stable lg:-mr-[29px] lg:pr-6"
    @scroll="onProfileScroll"
  >
    <user-profile-header
      ref="expandedProfileHeaderRef"
      :class="centerProfileState ? 'shrink-0' : ''"
      :user="profileUser"
      :is-current-user="isCurrentUser"
      :condensed="false"
      v-model:privacy="privacy"
    />

    <core-state-layout v-if="initialLoading">
      <core-contextual-loading-box loading-text="Loading Profile" sans-text />
    </core-state-layout>

    <template v-else>
      <div class="sticky top-0 z-30 mb-3 bg-white" :class="isCondensedHeader ? 'shadow-sm' : ''">
        <user-profile-header
          v-if="isCondensedHeader"
          :user="profileUser"
          :is-current-user="isCurrentUser"
          :condensed="true"
          v-model:privacy="privacy"
        />

        <div
          v-if="showTimeline"
          class="flex items-center gap-8 border-b border-slate-200 px-2 -mt-2"
          role="tablist"
          aria-label="Profile content"
        >
          <button
            v-for="tab in profileTabs"
            :id="`profile-tab-${tab.value}`"
            :key="tab.value"
            type="button"
            role="tab"
            class="-mb-px border-b-2 px-2 py-3 text-base font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heroblue-500 focus-visible:ring-offset-2"
            :class="activeView === tab.value ? 'border-heroblue-600 text-heroblue-700' : 'border-transparent text-slate-500 hover:text-slate-700'"
            :aria-selected="activeView === tab.value"
            :aria-controls="`profile-panel-${tab.value}`"
            @click="setActiveView(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <div :class="centerAlbumsState ? 'flex min-h-0 flex-1 flex-col' : ''">
        <section
          v-if="showTimeline"
          v-show="activeView === 'timeline'"
          id="profile-panel-timeline"
          role="tabpanel"
          aria-labelledby="profile-tab-timeline"
        >
          <user-profile-timeline
            :user-id="profileUser.slug || profileUser.id"
            :timeline-controller="profileTimelineController"
            :timeline-navigation="profileTimelineNavigation"
            :active="activeView === 'timeline'"
            :scroll-element="profileScrollEl"
            external-scroll
          />
        </section>

        <section
          v-show="activeView === 'albums'"
          id="profile-panel-albums"
          role="tabpanel"
          :aria-labelledby="showTimeline ? 'profile-tab-albums' : undefined"
          class="min-h-0"
          :class="centerAlbumsState ? 'flex flex-1 flex-col' : ''"
        >
          <transition-group name="f-slide-fade">
            <core-state-layout v-if="pending" key="loading">
              <core-contextual-loading-box loading-text="Loading Albums" />
            </core-state-layout>
            <core-state-layout v-else-if="error" key="error">
              <core-contextual-error-box :action="refresh" />
            </core-state-layout>

            <core-state-layout v-else-if="!albums.length && isCurrentUser && privacy === ALBUM_PERMISSION_LAYERS.private.value" key="owner-empty">
              <core-empty-state
                heading="Welcome to Your Profile"
                description="Get started by uploading files to your FOREVER Account. Then, head to Albums and create an album to start organizing. Your albums will display here as a beautiful gallery."
                icon="i-ri-account-circle-line"
              />
            </core-state-layout>

            <core-state-layout v-else-if="!albums.length" key="viewer-empty">
              <core-empty-state
                heading="Nothing to see here (yet)."
                :description="isFriend || privacy === ALBUM_PERMISSION_LAYERS.family.value ? `Once ${profileUser.name} creates albums shared with you, you'll be able to view them here.` : `Once ${profileUser.name} creates Public albums, you'll be able to view them here.`"
                icon="i-ri-account-circle-line"
              />
            </core-state-layout>

            <core-list
              v-else
              key="albums"
              :items="albums"
              :scroller-elem="profileScrollEl"
              :disable-approach-bottom="pending || isLoadingMore || !hasMore"
              :container-class="ALBUM_LIST_CONTAINER_CLASS"
              :section-size="ALBUM_LIST_SECTION_SIZE"
              :grid-gap-class="ALBUM_LIST_GAP_CLASS"
              @approach-bottom="getMore"
            >
              <template #default="{item: album}">
                <album-list-item
                  :key="album.id"
                  :album="album"
                  class="h-full"
                  :selectable="false"
                  @item-click="onItemClick(album)"
                />
              </template>
            </core-list>
          </transition-group>
        </section>
      </div>
    </template>
  </div>
</template>

<script setup>
  import {UserProfileConnectionIntentModal} from '#components';
  import {storeToRefs} from 'pinia';

  const route = useRoute();
  const router = useRouter();
  const userIdOrSlug = String(route.params.user_id_or_slug);
  const profileScrollEl = ref(null);
  const centerAlbumsState = computed(() => (
    activeView.value === 'albums'
    && (pending.value || error.value || !albums.value?.length)
  ));
  const centerProfileState = computed(() => initialLoading.value || centerAlbumsState.value);
  const expandedProfileHeaderRef = ref(null);

  const usersStore = useUsersStore();
  const {currentProfileUser} = storeToRefs(usersStore);
  const sessionStore = useSessionStore();
  const {currentUser, isAuthenticated} = storeToRefs(sessionStore);

  definePageMeta({
    alias: ['/invite/:user_id_or_slug()/:token?']
  });

  const {error: userError} = await useAsyncData(
    `user-profile: ${userIdOrSlug}`,
    () => usersStore.getUserProfile({userIdOrSlug})
  );

  if (userError.value) {
    throw createError({...userError.value});
  }

  const isCurrentUser = computed(() => currentProfileUser.value.id === sessionStore.currentUserId);
  const profileUser = computed(() => (
    isCurrentUser.value ? currentUser.value : currentProfileUser.value
  ));
  const friendStatus = computed(() => currentProfileUser.value.friendship?.status);
  const isFriend = computed(() => friendStatus.value === FRIEND_STATUS.completed);

  useForeverHead({
    title: computed(() => `${profileUser.value.name} | Profile`),
    description: computed(() => profileUser.value.description),
    socialImage: computed(() => profileUser.value.banner_images?.large?.url || profileUser.value.avatarImage)
  });

  const privacy = ref(isCurrentUser.value ? ALBUM_PERMISSION_LAYERS.private.value : null);
  const profileTimelineNavigation = useTimelineNavigation(computed(() => String(profileUser.value.id)), 'profile');
  const profileTimelineController = useTimeline(
    computed(() => profileUser.value.id),
    {forcedInitialPeriodType: profileTimelineNavigation.initialPeriodType}
  );
  const {
    visibility: timelineVisibility,
    pending: timelinePending,
    error: timelineError,
    displayedDecades: timelineDecades,
    displayedYears: timelineYears
  } = profileTimelineController;
  const timelineHasContent = computed(() => (
    timelineDecades.value.length > 0 || timelineYears.value.length > 0
  ));
  const timelineAccessDenied = computed(() => (
    Number(
      timelineError.value?.statusCode
      || timelineError.value?.status
      || timelineError.value?.response?.status
    ) === 403
  ));
  const ownerViewAllowsTimeline = computed(() => (
    profileUser.value.account_type === USER_ACCOUNT_TYPES.organization.value
    && timelineVisibility.value === 'connections'
    && [ALBUM_PERMISSION_LAYERS.private.value, ALBUM_PERMISSION_LAYERS.family.value].includes(privacy.value)
  ));
  const showTimeline = computed(() => (
    timelineHasContent.value
    && !timelineAccessDenied.value
    && (isCurrentUser.value ? ownerViewAllowsTimeline.value : true)
  ));
  const profileContentReady = computed(() => !timelinePending.value);

  const profileTabs = [
    {label: 'Timeline', value: 'timeline'},
    {label: 'Custom Albums', value: 'albums'}
  ];
  const activeView = computed(() => (
    showTimeline.value && route.query.view !== 'albums'
      ? 'timeline'
      : 'albums'
  ));

  watch([showTimeline, profileContentReady], async ([isVisible, isReady]) => {
    if (isReady && !isVisible && route.query.view === 'timeline') {
      await setActiveView('albums');
    }
  }, {immediate: true});

  async function setActiveView(view) {
    if (view === 'timeline' && !showTimeline.value) {
      return;
    }

    if (profileScrollEl.value) {
      profileScrollPositions[activeView.value] = profileScrollEl.value.scrollTop;
    }

    await router.replace({
      query: {
        ...route.query,
        view
      }
    });
  }

  const isCondensedHeader = ref(false);
  const profileScrollPositions = reactive({albums: 0, timeline: 0});

  function getExpandedHeaderHeight() {
    const header = expandedProfileHeaderRef.value?.$el || expandedProfileHeaderRef.value;
    return header?.offsetHeight || 0;
  }

  function updateStickyHeader() {
    isCondensedHeader.value = profileScrollEl.value?.scrollTop >= getExpandedHeaderHeight();
  }

  function onProfileScroll(event) {
    profileScrollPositions[activeView.value] = event.currentTarget.scrollTop;
    updateStickyHeader();
  }

  watch(activeView, async view => {
    await nextTick();
    if (!profileScrollEl.value) {
      return;
    }

    if (view !== 'timeline' || !profileTimelineNavigation.navigationState.value) {
      profileScrollEl.value.scrollTop = profileScrollPositions[view] || 0;
    }
    updateStickyHeader();
  });

  onMounted(() => {
    updateStickyHeader();
  });

  const albumsStore = useAlbumsStore();
  const {albums, hasMore} = storeToRefs(albumsStore);
  const {refresh, pending, error, status: albumsStatus} = await useLazyAsyncData(
    `albums-${privacy.value || 'public'}-${currentProfileUser.value.id}`,
    () => albumsStore.getAlbums({
      privacy,
      userId: currentProfileUser.value.id
    }),
    {watch: [privacy]}
  );

  const initialProfileStatus = computed(() => {
    if (!profileContentReady.value) return 'pending';
    // Timeline eligibility must settle first, but a selected Timeline need not wait for Albums.
    return activeView.value === 'timeline' ? 'success' : albumsStatus.value;
  });
  const {initialLoading} = useInitialLoadState(initialProfileStatus);

  const isLoadingMore = ref(false);
  async function getMore() {
    if (!hasMore.value) {
      return;
    }

    isLoadingMore.value = true;
    try {
      await albumsStore.getAlbums({nextPage: true});
    } catch (e) {
      useErrorToast().add('There was an issue getting more albums.');
    } finally {
      isLoadingMore.value = false;
    }
  }

  function onItemClick(album) {
    navigateTo(useBuildRoute().toAlbum({
      album,
      userId: profileUser.value.slug || profileUser.value.id,
      query: {source: 'profile'}
    }));
  }

  async function consumeConnectionIntent() {
    if (route.query.connect !== 'true') {
      return;
    }

    const query = {...route.query};
    delete query.connect;
    await router.replace({query});
  }

  function takePendingSignInIntent() {
    if (!process.client) {
      return false;
    }

    const storedValue = sessionStorage.getItem(PROFILE_CONNECTION_INTENT_STORAGE_KEY);
    if (!storedValue) {
      return false;
    }

    try {
      const intent = JSON.parse(storedValue);
      const isCurrentIntent = String(intent.userId) === String(profileUser.value.id);
      const isFresh = Date.now() - intent.createdAt <= PROFILE_CONNECTION_INTENT_MAX_AGE;

      if (isCurrentIntent || !isFresh) {
        sessionStorage.removeItem(PROFILE_CONNECTION_INTENT_STORAGE_KEY);
      }

      return isCurrentIntent && isFresh;
    } catch (e) {
      sessionStorage.removeItem(PROFILE_CONNECTION_INTENT_STORAGE_KEY);
      return false;
    }
  }

  function openAuthenticatedConnectionIntent() {
    useFModal({
      id: 'profile-connection-intent',
      component: UserProfileConnectionIntentModal,
      size: 'sm-md',
      backdropClass: BACKDROP_BLUR_CLASS,
      roundedClass: 'rounded-xl',
      clickToClose: false,
      escToClose: false,
      props: {
        user: profileUser.value
      },
      onClose: consumeConnectionIntent
    }).open();
  }

  async function handleConnectionIntent() {
    if (route.query.connect !== 'true') {
      return;
    }

    const relationshipHandled = [
      FRIEND_STATUS.completed,
      FRIEND_STATUS.sent,
      FRIEND_STATUS.pending
    ].includes(friendStatus.value);

    if (isCurrentUser.value || relationshipHandled) {
      await consumeConnectionIntent();
      return;
    }

    if (!isAuthenticated.value) {
      useCommonModals().openSignupFromShareModal({
        referrer: profileUser.value,
        referralSource: 'connection-add',
        sendConnectionRequest: true,
        escToClose: false,
        onClose: consumeConnectionIntent
      });
      return;
    }

    if (takePendingSignInIntent()) {
      try {
        await usersStore.addConnection({user: profileUser.value});
        await consumeConnectionIntent();
        return;
      } catch (e) {
        useErrorToast().add();
      }
    }

    openAuthenticatedConnectionIntent();
  }

  const backToTop = useBackToTop();
  onMounted(async () => {
    backToTop.bindToScroller({elem: profileScrollEl.value});

    if (route.params.token) {
      const referralToken = route.params.token;
      if (isCurrentUser.value) {
        navigateTo(useBuildRoute().toUserProfile({user: {slug: currentUser.value.slug}}));
      } else if (isAuthenticated.value && isFriend.value) {
        navigateTo(useBuildRoute().toUserProfile({user: {slug: currentProfileUser.value.slug}}));
      } else {
        await useCommonModals().openSignupFromShareModal({
          referrer: currentProfileUser.value,
          referralToken,
          referralSource: 'connection-link'
        });
      }
    } else {
      await handleConnectionIntent();
    }
  });

  onBeforeUnmount(() => backToTop.unbind());
</script>
